import { useToast } from "@chakra-ui/react";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { Heading } from "@equidefi/ui";

import Spinner from "@/components/spinner";
import { getReturnTo } from "@/helpers/routing";
import { useProcessStripePayment } from "@/hooks/useInvestments";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

import { WorkflowContainer } from "../WorkflowContainer";
import { useAuth } from "@equidefi/portals/hooks/useAuth";

const WorkflowPaymentProcessing = () => {
  const { investment_id: investmentId } = useParams();
  const { investment, offering, user, refetchInvestment, analytics } = useWorkflowContext();
  const navigate = useNavigate();
  const effectRan = useRef(false);

  const auth = useAuth();

  const [searchParams] = useSearchParams();
  const toast = useToast();

  const handleNavigate = useCallback(
    (investment, nextStep) => {
      const returnTo = getReturnTo(nextStep);
      const step = investment.expedited ? "agreement" : returnTo;
      const target = `/offerings/${offering.slug}/${investmentId}/${step}`;

      navigate(target, { replace: true });
    },
    [navigate, offering, investmentId],
  );

  const processPayment = useProcessStripePayment(investment.id, {
    onSuccess: async (nextStep) => {
      analytics.track("payment", {
        amount: investment.amount,
        type: "STRIPE",
      });
      const { data: updated } = await refetchInvestment();
      const target = updated ?? investment;
      handleNavigate(target, nextStep);
    },
    onError: () => {
      toast({ status: "error", description: "Payment failed to process." });
      navigate(`/offerings/${offering.slug}/${investmentId}/payment`, {
        replace: true,
        state: { returnTo: getReturnTo(investment.next) },
      });
    },
  });

  useEffect(() => {
    const run = async () => {
      if (searchParams.get("crypto")) {
        // Refresh before forwarding so investment.next reflects the just-made
        // payment. CryptoPayment navigates here without refetching (to avoid the
        // "already submitted" flash on the payment step), so this page owns the
        // refresh — otherwise stale workflow state bounces the investor back to
        // the payment step.
        const { data: updated } = await refetchInvestment();
        toast({
          status: "success",
          description: "Crypto payment is processing.",
        });
        const target = updated ?? investment;
        handleNavigate(target, target.next);
        return;
      }

      await processPayment.mutateAsync(searchParams.get("payment_intent"));
    };

    if (!effectRan.current) {
      // to avoid double execution in StrictMode/development
      run();
    }

    return () => {
      effectRan.current = true;
    };
  }, []);

  return (
    <WorkflowContainer>
      <Heading fontWeight="bold" textStyle="h1" textAlign="center">
        Payment Processing...
      </Heading>
      <Spinner show />
    </WorkflowContainer>
  );
};

export default WorkflowPaymentProcessing;
